import React, { useEffect, useState } from "react";
import { Footer, Navbar } from "../components";
import { useSelector, useDispatch } from "react-redux";
import { addCart, delCart, setCart } from "../redux/action";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api from "../utils/base_url";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import '../styles/index.css';
import "../styles/Cart.css";

// Turn a `reserved_items` / `valid_items` line from the backend
// ({type, id, qty, product_name, price, offer_price, original_price, product_image})
// into the cart item shape used across the app.
const mapReservedToCartItems = (items = []) =>
  items.map((i) => ({
    id: `${i.type}-${i.id}`,
    refId: i.id,
    itemType: i.type,
    qty: i.qty,
    product_name: i.product_name,
    price: Number(i.price),
    offer_price: i.offer_price !== null && i.offer_price !== undefined ? Number(i.offer_price) : null,
    original_price: i.original_price !== null && i.original_price !== undefined ? Number(i.original_price) : null,
    product_image: i.product_image,
  }));

const ShowCart = ({
  state,
  stocks,
  stockError,
  disabledQty,
  addItem,
  removeItem,
  formatName,
  initiateCheckout,
  navigate
}) => {
  let subtotal = 0;

  state.forEach((item) => {
    const priceToUse =
      item.offer_price && Number(item.offer_price) > 0
        ? Number(item.offer_price)
        : Number(item.price);
    subtotal += priceToUse * item.qty;
  });


  const MIN_ORDER_VALUE = 500;

  return (
    <section className="h-100 gradient-custom-themed">
      <div className="container py-5">
        <div className="row d-flex justify-content-center my-4">

          {/* Cart items */}
          <div className="col-lg-8 col-md-10 col-12 mb-4">
            <div className="card mb-4 rounded-theme shadow-theme">
              <div className="card-header py-3 cart-card-header">
                <h5 className="mb-0 cart-card-title">
                  Item List
                </h5>
              </div>
              <div className="card-body p-3">
                {state.map((item) => {
                  const priceToUse =
                    item.offer_price && Number(item.offer_price) > 0
                      ? Number(item.offer_price)
                      : Number(item.price);

                  // Strikethrough price: either a product's original price
                  // (when an offer applies) or a combo's "you save" price.
                  const originalPrice =
                    item.offer_price && Number(item.offer_price) > 0
                      ? Number(item.price)
                      : item.original_price && Number(item.original_price) > Number(item.price)
                        ? Number(item.original_price)
                        : null;

                  return (
                    <div
                      key={item.id}
                      className="mb-3 pb-3 border-bottom border-theme-light cart-clickable-item"
                      onClick={() =>
                        navigate(item.itemType === "combo" ? `/combo/${item.refId}` : `/product/${item.refId}`)
                      }
                    >

                      <div className="row align-items-center g-2">
                        <div className="col-4 col-sm-3 text-center">
                          <img
                            src={`${item.product_image}`}
                            alt={formatName(item.product_name)}
                            className="cart-product-image rounded-theme img-fluid"
                          />
                        </div>

                        <div className="col-8 col-sm-5">
                          <p className="cart-product-title" >
                            {formatName(item.product_name)}
                          </p>

                          {item.quantity && item.quantity_unit && (
                            <p className="cart-qty-unit">
                              {["size", "color"].includes(
                                item.quantity_unit?.toLowerCase()
                              )
                                ? item.quantity
                                : `${item.quantity} ${item.quantity_unit}`}
                            </p>
                          )}
                          <p className="cart-price-text">
                            ₹ {priceToUse.toLocaleString()}{" "}
                            {originalPrice && (
                              <span className="cart-original-price">
                                ₹ {originalPrice.toLocaleString()}
                              </span>
                            )}
                          </p>
                        </div>

                        <div className="col-12 col-sm-4 d-flex align-items-center justify-content-between justify-content-sm-end gap-2">
                          <div className="input-group input-group-sm quantity-controls">
                            {item.qty > 1 ? (
                              <button
                                className="btn btn-themed-outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeItem(item);
                                }}
                                aria-label={`Remove one ${item.product_name}`}
                              >
                                <i className="fas fa-minus"></i>
                              </button>
                            ) : (
                              <button
                                className="btn btn-themed-outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeItem(item);
                                }}
                                aria-label={`Remove ${item.product_name} from cart`}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            )}

                            <span className="input-group-text qty-display">
                              {item.qty}
                            </span>
                            <button
                              className="btn btn-themed-outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                addItem(item);
                              }}
                              disabled={disabledQty[item.id]}
                            >
                              <i className="fas fa-plus"></i>
                            </button>
                          </div>

                          <p
                            className="cart-total-price"
                          >
                            ₹ {(priceToUse * item.qty).toLocaleString()}
                          </p>

                          {stockError[item.id] && (
                            <motion.div
                              initial={{ x: 80, opacity: 0 }}
                              animate={{ x: [80, 0, -6, 6, -6, 6, 0], opacity: 1 }}
                              transition={{ duration: 0.6, ease: "easeOut" }}
                              className="stock-error-badge"
                            >
                              Stock limit reached!
                            </motion.div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="col-lg-4 col-md-8 col-12 mb-4">
            <div className="card rounded-theme shadow-theme">
              <div className="card-header py-3 cart-card-header">
                <h5 className="mb-0 cart-card-title">
                  Order Summary
                </h5>
              </div>
              <div className="card-body p-3">
                <ul className="list-group list-group-flush mb-3">

                  {/* Product subtotal */}
                  <li className="list-group-item d-flex justify-content-between px-0">
                    Product Amount
                    <span>₹{Math.round(subtotal).toLocaleString()}</span>
                  </li>

                  {/* Courier charge — informational text only, no value */}
                  <li className="list-group-item d-flex justify-content-between px-0 align-items-center">
                    <span>Courier Charge</span>
                    <span className="courier-note">
                      calculated at checkout
                    </span>
                  </li>

                  {/* Total — product amount only, courier added at checkout */}
                  <li className="list-group-item d-flex justify-content-between px-0 fw-bold align-items-center">
                    <span>Sub Total</span>
                    <span>
                      ₹{Math.round(subtotal).toLocaleString()}{" "}
                      <span className="subtotal-note">
                        + courier
                      </span>
                    </span>
                  </li>
                </ul>

                <button
                  className="btn-themed btn-lg w-100 text-center"
                  onClick={initiateCheckout}
                  disabled={subtotal < MIN_ORDER_VALUE}
                >
                  {subtotal < MIN_ORDER_VALUE
                    ? `Add ₹${(
                      MIN_ORDER_VALUE - subtotal
                    ).toLocaleString()} more`
                    : "Go to checkout"}
                </button>

                {subtotal < MIN_ORDER_VALUE && (
                  <p className="minimum-order-warning">
                    Minimum order value is ₹500
                  </p>
                )}

                {/* Helper note below button */}
                <p className="delivery-helper-text">
                  Delivery charge will be added based on your pincode
                </p>

              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

const Cart = () => {

  const [stocks, setStocks] = useState({});
  const [stockError, setStockError] = useState({});
  const [disabledQty, setDisabledQty] = useState({});

  const state = useSelector((state) => state.handleCart);
  const dispatch = useDispatch();

  const { user } = useAuth();
  const location = useLocation();

  const addItem = (item) => {
    const availableStock = stocks[item.id];
    const nextQty = item.qty + 1;

    if (availableStock !== undefined && nextQty > availableStock) {
      setStockError(prev => ({ ...prev, [item.id]: true }));
      setDisabledQty(prev => ({ ...prev, [item.id]: true }));

      setTimeout(() => {
        setStockError(prev => ({ ...prev, [item.id]: false }));
        setDisabledQty(prev => ({ ...prev, [item.id]: false }));
      }, 2000);

      return;
    }

    dispatch(addCart(item));
  };

  const navigate = useNavigate();

  const initiateCheckout = async () => {
    const token = sessionStorage.getItem("access");
    if (!token) {
      sessionStorage.setItem(
        "redirect_toast",
        "Please login first to continue checkout"
      );

      sessionStorage.setItem(
        "login_redirect",
        JSON.stringify({
          pathname: location.pathname,
          search: location.search,
          state: location.state,
        })
      );

      return navigate("/login");
    }

    const userId = user.id;
    const cartPayload = state.map((item) =>
      item.itemType === "combo"
        ? { combo: item.refId, quantity: item.qty }
        : { product: item.refId, quantity: item.qty }
    );

    try {
      const res = await api.post("checkout-initiate/", {
        user_id: userId,
        cart: cartPayload,
      });

      if (!res.data.status) {
        toast.error(res.data.message || "Cart updated due to stock change. Please review and click checkout again.");

        const final = mapReservedToCartItems(res.data.reserved_items);
        dispatch(setCart(final));
        return;
      }

      navigate("/checkout");
    } catch (err) {
      console.error("initiateCheckout error:", err);
      toast.error("Unable to start checkout");
    }
  };

  const formatName = (text) => {
    if (!text) return "";
    const cleaned = text.replace(/_/g, " ").toLowerCase();
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  };

  const removeItem = (item) => {
    dispatch(delCart(item));
    setStockError(prev => ({ ...prev, [item.id]: false }));
  };

  const EmptyCart = () => (
    <div className="empty-cart-wrapper">
      <div className="empty-cart-card">

        <div className="empty-cart-icon">
          <i className="fas fa-shopping-bag"></i>
        </div>

        <h4 className="empty-cart-title">
          Your Cart is Empty
        </h4>

        <p className="empty-cart-subtitle">
          Looks like you haven’t added anything yet.
        </p>

        <Link
          to="/product"
          className="empty-cart-btn"
        >
          <i className="fa fa-arrow-left"></i>
          Continue Shopping
        </Link>
      </div>
    </div>
  );


  useEffect(() => {
    const getStock = async () => {
      try {
        const stockData = {};
        for (const item of state) {
          if (item.itemType === "combo") {
            const res = await api.get(`combolist/${item.refId}/`);
            stockData[item.id] = res.data?.data?.available_stock ?? 0;
          } else {
            const res = await api.get(`stock/?product_id=${item.refId}`);
            stockData[item.id] = res.data.stock;
          }
        }
        setStocks(stockData);
      } catch (error) {
        console.error("Stock fetch error:", error);
      }
    };

    if (state.length > 0) {
      getStock();
    }
  }, [state]);

  return (
    <>
      <Navbar />
      <div className="cart-page">
        <div className="container my-3 py-3">
          <h1 className="cart-page-title">
            Cart
          </h1>
          <hr className="border-theme-light" />
          {state.length > 0 ? (
            <ShowCart
              state={state}
              stocks={stocks}
              stockError={stockError}
              disabledQty={disabledQty}
              addItem={addItem}
              removeItem={removeItem}
              formatName={formatName}
              initiateCheckout={initiateCheckout}
              navigate={navigate}
            />
          ) : <EmptyCart />}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Cart;